import React, { DOMAttributes, forwardRef } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';

import { bemCssModules, getTestId } from '@utils';

import { CryptoCurrencyEnum, CurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';

import CurrencySelect from '@components/CurrencySelect';
import NewInput from '@components/NewInput';

import style from './MoneyInput.scss';

const cn = bemCssModules(style, 'MoneyInput');
const ti = getTestId('MoneyInput');

export type TMoneyInput = {
  className?: string;
  totalDigits?: number;
  displayDigits?: number;
  currency: string;
  currencyNetwork?: string;
  disabled?: boolean;
  onCurrencyChange?: (currency: CryptoCurrencyEnum | FiatCurrencyEnum) => void;
  disabledChangeCurrency?: boolean;
  currencies: {
    value: CurrencyEnum;
    label: CurrencyEnum;
  }[];
  error?: boolean;
  currencySelectIsLoading?: boolean;
  value: string;
  onChange: (event: string) => void;
  testId?: string;
  onFocus?: () => void;
  onBlur?: DOMAttributes<HTMLInputElement>['onBlur'];
};

const MoneyInput = forwardRef<HTMLInputElement, TMoneyInput>((props, ref) => {
  const {
    className,
    totalDigits = 2,
    displayDigits = 2,
    value,
    currencyNetwork,
    onChange = () => {},
    currency,
    onCurrencyChange = () => {},
    disabledChangeCurrency,
    currencies,
    disabled,
    error,
    currencySelectIsLoading,
    testId,
    ...rest
  } = props;

  const numberTemplate = new RegExp(
    `^\\d*${totalDigits ? `[.,]?` : ''}(\\d{0,${totalDigits}})?$`,
    ''
  );

  const filterInput = (val: string): void => {
    let result = val;
    if (totalDigits > 0) {
      if (!/\./.test(val)) {
        result = val
          .replace(/^[,бю]/, '0.')
          .replace(/[,бю]/g, '.')
          .replace(/[^\d.]/, '')
          .replace(/^0(\d{1})/, '0.$1');
      } else {
        result = val.replace(/^0{1,}/, '0');
        const pointIndex = result.indexOf('.');
        result = `${result.slice(0, pointIndex)}.${result.slice(
          pointIndex + 1,
          pointIndex + 1 + totalDigits
        )}`;
        if (val.length > value.length) {
          result = result.replace(/^\./, '0.');
        } else {
          result = result.replace(/^\./, '');
        }
      }
    } else {
      result = val
        .replace(/^0\./, '')
        .replace(/^0{1,}/, '')
        .replace(/\./, '');
    }
    if (numberTemplate.test(result) || val.length < value.length) onChange(result);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    filterInput(event.target.value);
  };

  return (
    <div className={cn(null, [className])}>
      <NewInput
        ref={ref}
        className={cn('input')}
        placeholder={correctingTrailingNumbers(
          totalDigits > 0 ? `0.${'0'.repeat(totalDigits)}` : '0',
          displayDigits,
          totalDigits
        )}
        autoComplete="off"
        inputMode="decimal"
        onChange={handleChange}
        value={value}
        disabled={disabled}
        error={error}
        testId={ti(testId)}
        {...rest}
      />
      <CurrencySelect
        className={cn('select')}
        value={currency}
        network={currencyNetwork}
        onClick={onCurrencyChange}
        disabled={disabledChangeCurrency}
        error={!!error}
        currencies={currencies}
        isLoading={currencySelectIsLoading}
        testId={ti(testId)}
      />
    </div>
  );
});

export default MoneyInput;
